import React, { useCallback, useEffect, useState } from 'react';
import AppLayout from '../../../../component/appLayout';
import { ColumnContent, Group, Header, PersonalText, Container } from '../../styles';
import Button from '../../../../component/button';
import {
  BottomContent,
  BottomDiv,
  Box,
  Card,
  Content,
  Description,
  Icon,
  LeftContent,
  Leftdata,
  Price,
  RightContent,
  Row,
  RowData,
  Title,
  TopPlan
} from './styles';
import SVGIcon from '../../../../assets/images/svg/SVGIcon';
import { createBillingPortalSession, getSubscriptionList } from '../../../../services/settingServices';
import dayjs from 'dayjs';
import { countDays, getUrlParam, isEmpty } from '../../../../helpers/common';
import SubScriptionLoading from '../../../../component/loading/subScriptionLoading';
import { captureException } from '../../../../services/logService';
import ResponsiveHeader from '../../../../component/responsiveHeader';
import { useMobileDevice } from '../../../../global/useMobile';
import { useHistory } from 'react-router-dom';
import BottomSubNavigation from '../../../../component/bottomNavigation/subNavigation';
import { BUTTON_TYPE, EMPTY_STATE_SIZE, ICON_SIZE } from '../../../../global/constants';
import { rootStore } from '../../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import EmptyStateV2 from '../../../../component/emptyState';

const Subscription: React.FC = () => {
  // States
  const [loading, setLoading] = useState(false);
  const [managingCustomerId, setManagingCustomerId] = useState<string | null>(null);

  //mobx states
  const { workspaceStore, appStore, settingStore } = rootStore;
  const { currentWorkspace } = workspaceStore;
  const { themeMode } = appStore || {};
  const { subscriptionList } = settingStore;

  // Other variables
  const isMobile = useMobileDevice();
  const history = useHistory();
  const status = getUrlParam(history.location.search, 'status');

  // Loads subscription data for the workspace
  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      if (!isEmpty(currentWorkspace?.id)) {
        await getSubscriptionList();
      }
    } catch (error) {
      captureException(error);
    } finally {
      if (!isEmpty(currentWorkspace?.id)) setLoading(false);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Opens the Stripe Customer Billing Portal for a live subscription row
  const handleManageSubscription = useCallback(async (customerId: string) => {
    setManagingCustomerId(customerId);
    try {
      const url = await createBillingPortalSession(customerId);
      if (url) {
        window.location.href = url;
        return;
      }
    } catch (error) {
      captureException(error);
    }
    setManagingCustomerId(null);
  }, []);

  return (
    <>
      <AppLayout isPosition={true}>
        <Container className='desktop-content'>
          {!isMobile ? (
            <Header applyMargin={true}>
              <PersonalText>Subscription</PersonalText>
            </Header>
          ) : (
            <ResponsiveHeader title='Subscription' />
          )}
          <Group>
            <Content>
              {loading && <SubScriptionLoading />}
              {/* Payment Successful card */}
              {(!loading ||
                (subscriptionList?.activeSubscription && subscriptionList?.activeSubscription?.length > 0) ||
                (subscriptionList?.pastSubscription && subscriptionList?.pastSubscription?.length > 0)) && (
                <>
                  {status && status === 'success' && (
                    <Card>
                      <Icon>
                        <SVGIcon name='successful-icon' width='42' height='42' viewBox='0 0 42 42' />
                      </Icon>
                      <BottomDiv>
                        <h1>Payment Successful! </h1>
                        <p>Your subscription is now active. Enjoy seamless access to all premium features!</p>
                      </BottomDiv>
                    </Card>
                  )}
                  {/* Payment failed card */}
                  {status && status === 'failed' && (
                    <Card>
                      <Icon>
                        <SVGIcon
                          name='failed-icon'
                          width='42'
                          height='42'
                          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                          fill='var(--accent-error)'
                        />
                      </Icon>
                      <BottomDiv>
                        <h1>Payment Failed </h1>
                        <p>
                          There was an issue with your payment. Update your details to keep your access uninterrupted.
                        </p>
                      </BottomDiv>
                      <Button title='Retry' variant={BUTTON_TYPE.SECONDARY} />
                    </Card>
                  )}
                </>
              )}
              <BottomContent>
                {!loading &&
                  subscriptionList?.activeSubscription &&
                  subscriptionList?.activeSubscription?.length > 0 &&
                  subscriptionList?.activeSubscription?.map((item) => {
                    const daysDifference = countDays(item?.end);
                    const isLifetime = item?.planName?.toLowerCase().includes('lifetime');
                    return (
                      <Box key={item?.id}>
                        <LeftContent>
                          <h1>
                            {item?.planName} - for {Number(item?.users) < 0 ? 'Unlimited' : item?.users} Users
                          </h1>
                          <Description>
                            <p>{isLifetime ? 'Lifetime' : `${daysDifference} days remaining`}</p>
                          </Description>
                        </LeftContent>
                        {!isEmpty(item?.customerId) && (
                          <Button
                            title='Manage Subscription'
                            variant={BUTTON_TYPE.SECONDARY}
                            onClick={() => handleManageSubscription(item.customerId as string)}
                            isLoading={managingCustomerId === item?.customerId}
                            disabled={!isEmpty(managingCustomerId)}
                          />
                        )}
                      </Box>
                    );
                  })}
              </BottomContent>
              {false && !loading && subscriptionList?.activeSubscription?.length === 0 && (
                <TopPlan>
                  <Leftdata>
                    <h1>Unlimited</h1>
                    <p>
                      Choose a plan that aligns with your goals and enjoy the full power of Teamcamp without any hidden
                      costs.
                    </p>
                    <RowData>
                      <Row>
                        <SVGIcon
                          name='correct-sign-icon'
                          width={ICON_SIZE.MD}
                          height={ICON_SIZE.MD}
                          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                          fill='var(--text-primary)'
                        />
                        <p>Unlimited user, project & task</p>
                      </Row>
                      <Row>
                        <SVGIcon
                          name='correct-sign-icon'
                          width={ICON_SIZE.MD}
                          height={ICON_SIZE.MD}
                          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                          fill='var(--text-primary)'
                        />
                        <p>Desktop time tracker</p>
                      </Row>
                      <Row>
                        <SVGIcon
                          name='correct-sign-icon'
                          width={ICON_SIZE.MD}
                          height={ICON_SIZE.MD}
                          viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                          fill='var(--text-primary)'
                        />
                        <p>Invoicing and Payment</p>
                      </Row>
                    </RowData>
                    <Button title='Upgrade' variant={BUTTON_TYPE.PRIMARY} />
                  </Leftdata>
                  <RightContent>
                    <Price>
                      $59 <span>/ month</span>
                    </Price>
                    {themeMode?.theme === 'dark' ? (
                      <SVGIcon
                        name='dark-background-subscription'
                        width='662'
                        height='150'
                        viewBox='0 0 662 150'
                        className='background-image'
                      />
                    ) : (
                      <SVGIcon
                        name='light-background-subscription'
                        width='662'
                        height='150'
                        viewBox='0 0 662 150'
                        className='background-image'
                      />
                    )}
                  </RightContent>
                </TopPlan>
              )}
              {!loading &&
                subscriptionList?.activeSubscription?.length === 0 &&
                subscriptionList?.pastSubscription?.length === 0 && (
                  <EmptyStateV2
                    size={EMPTY_STATE_SIZE.MEDIUM}
                    title="You don't have any subscription"
                    description='There are no subscriptions'
                    imageUrl='mytask_empty'
                    isAlignedCenter={true}
                  />
                )}
              {!loading && subscriptionList?.pastSubscription && subscriptionList?.pastSubscription?.length > 0 && (
                <ColumnContent>
                  <Title>Past Subscription</Title>
                  <BottomContent>
                    {subscriptionList?.pastSubscription?.map((item) => {
                      const daysDifference = countDays(item?.end);
                      return (
                        <Box key={item?.id}>
                          <LeftContent>
                            <h1>
                              {item?.planName} - for {Number(item?.users) < 0 ? 'Unlimited' : item?.users} Users
                            </h1>
                            {daysDifference > 0 ? (
                              <Description>
                                <p> {daysDifference} days remaining </p>:
                              </Description>
                            ) : (
                              <Description>
                                <p>{dayjs(item?.end).format('MMM DD, YYYY')}</p>
                              </Description>
                            )}
                          </LeftContent>
                        </Box>
                      );
                    })}
                  </BottomContent>
                </ColumnContent>
              )}
            </Content>
          </Group>
        </Container>
      </AppLayout>
      <BottomSubNavigation />
    </>
  );
};

export default observer(Subscription);
